import { Image } from "antd";
import { Eye } from "lucide-react";
import { Button } from "../../components/ui/button";
import { DialogModal } from "../../components/ui/dialogModal";
import { Textarea } from "../../components/ui/input";
import { cn } from "../../utils/tailwindUtils";

const typeLabel = (value) => {
    if (value === "work_from_home") return "Work From Home";
    if (value === "permission") return "Permission";
    return "Leave";
};

const typeClass = (value) => {
    if (value === "work_from_home") {
        return "bg-purple-100 text-purple-800 border-purple-300 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-700";
    }
    if (value === "permission") {
        return "bg-sidebar/10 text-sidebar-primary border-sidebar-primary/30";
    }
    return "bg-green-100 text-green-800 border-green-300 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700";
};

const fileNameFromUrl = (url, index) => {
    try {
        return new URL(url).pathname.split("/").pop() || `Document ${index + 1}`;
    } catch {
        return `Document ${index + 1}`;
    }
};

const isImageUrl = (url) => {
    const name = fileNameFromUrl(url, 0);
    const extension = name.split(".").pop()?.toLowerCase();
    return ["jpg", "jpeg", "png", "gif", "webp"].includes(extension);
};

const FieldRow = ({ label, children }) => (
    <div className="flex justify-between gap-3 text-xs">
        <span className="text-gray-600 dark:text-gray-300">{label}:</span>
        <span className="text-right font-semibold text-gray-800 dark:text-white">{children}</span>
    </div>
);

export const LeaveApprovalReviewDialog = ({
    open,
    request,
    notes,
    onNotesChange,
    loading,
    onClose,
    onApprove,
    onReject,
}) => {
    if (!open || !request) return null;

    const documents = request.uploaded_documents || [];
    const status = request.status || request.approval_status || "pending";
    const canDecide = status === "pending";

    return (
        <DialogModal
            open={open}
            onClose={onClose}
            onSave={canDecide ? onApprove : undefined}
            onReject={canDecide ? onReject : undefined}
            title="Review Leave Request"
            saveLabel="Approve"
            rejectLabel="Reject"
            hideSaveButton={!canDecide}
            width="60rem"
            className="max-w-[95vw]"
            overlayClassName="z-[70]"
            loading={loading}
        >
            <div className="max-h-[70vh] space-y-4 overflow-y-auto pr-1">
                <div className="flex items-center justify-between rounded-lg border border-sidebar-primary/25 bg-gradient-to-r from-sidebar/10 to-sidebar/5 p-2 dark:border-sidebar-primary/30 dark:from-sidebar/20 dark:to-sidebar/10">
                    <div>
                        <h3 className="text-sm font-semibold text-gray-800 dark:text-white">Leave Request Review</h3>
                        <p className="text-xs text-gray-600 dark:text-gray-300">Please review all details carefully</p>
                    </div>
                    <div className="rounded-full border border-yellow-300 bg-yellow-100 px-3 py-1 text-xs font-medium text-yellow-800 dark:border-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300">
                        {String(status).charAt(0).toUpperCase() + String(status).slice(1)}
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
                    <div className="space-y-4">
                        <div className="rounded-lg border border-sidebar-primary/25 bg-gradient-to-br from-sidebar/10 to-sidebar/5 p-4 dark:border-sidebar-primary/30">
                            <h4 className="mb-3 text-xs font-semibold text-gray-800 dark:text-white">Employee Information</h4>
                            <div className="space-y-2">
                                <FieldRow label="Name">{request.request_from?.employee_name || request.employee || "-"}</FieldRow>
                                <FieldRow label="Employee ID">{request.request_from?.employee_id || "-"}</FieldRow>
                                <FieldRow label="Department">{request.request_from?.department?.name || "-"}</FieldRow>
                                <FieldRow label="Designation">{request.request_from?.designation?.name || "-"}</FieldRow>
                            </div>
                        </div>
                        <div className="rounded-lg border border-purple-200 bg-gradient-to-br from-purple-50 to-violet-50 p-4 dark:border-purple-700 dark:from-purple-900/10 dark:to-violet-900/10">
                            <h4 className="mb-3 text-xs font-semibold text-gray-800 dark:text-white">Request Details</h4>
                            <div className="space-y-2 text-xs">
                                <div className="flex items-center justify-between gap-3">
                                    <span className="text-gray-600 dark:text-gray-300">Type:</span>
                                    <span className={cn("rounded border px-2 py-1 text-xs font-medium", typeClass(request.leave_request_type))}>
                                        {typeLabel(request.leave_request_type)}
                                    </span>
                                </div>
                                <FieldRow label="Leave Type">{request.leave_type || "-"}</FieldRow>
                                <FieldRow label="Requested On">{request.requested_date || "-"}</FieldRow>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <div className="rounded-lg border border-green-200 bg-gradient-to-br from-green-50 to-emerald-50 p-4 dark:border-green-700 dark:from-green-900/10">
                            <h4 className="mb-3 text-xs font-semibold text-gray-800 dark:text-white">Duration & Timing</h4>
                            <div className="grid grid-cols-2 gap-3 text-center">
                                <div className="rounded-lg border border-sidebar-primary/25 bg-sidebar/10 p-3">
                                    <p className="mb-1 text-[10px] text-muted-foreground">Start Date</p>
                                    <p className="text-xs font-semibold">{request.from_date || "-"}</p>
                                </div>
                                <div className="rounded-lg border border-orange-200 bg-orange-50 p-3 dark:border-orange-600 dark:bg-orange-900/20">
                                    <p className="mb-1 text-[10px] text-muted-foreground">End Date</p>
                                    <p className="text-xs font-semibold">{request.to_date || "-"}</p>
                                </div>
                            </div>
                            {request.period && request.period !== "-" && (
                                <p className="mt-3 text-center text-xs capitalize text-muted-foreground">
                                    {String(request.period).replace("_", " ")}
                                </p>
                            )}
                        </div>
                        {documents.length > 0 && (
                            <div className="rounded-lg border border-violet-200 p-4 dark:border-violet-700/60">
                                <h4 className="mb-3 text-xs font-semibold">Attached Documents</h4>
                                <div className="space-y-2">
                                    {documents.map((docUrl, index) => {
                                        const fileName = fileNameFromUrl(docUrl, index);
                                        return (
                                            <div key={`${docUrl}-${index}`} className="flex items-center justify-between gap-2 rounded border px-2 py-1.5">
                                                <span className="truncate text-xs">{fileName}</span>
                                                {isImageUrl(docUrl) ? (
                                                    <Image
                                                        width={72}
                                                        src={docUrl}
                                                        alt={fileName}
                                                        className="rounded"
                                                        preview={{ mask: <Eye className="h-3 w-3" /> }}
                                                    />
                                                ) : (
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-6 px-2 text-xs"
                                                        onClick={() => window.open(docUrl, "_blank", "noopener,noreferrer")}
                                                    >
                                                        View
                                                    </Button>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="space-y-4">
                        <div className="rounded-lg border border-amber-200 bg-gradient-to-br from-amber-50 to-yellow-50 p-4 dark:border-amber-700 dark:from-amber-900/10">
                            <h4 className="mb-3 text-xs font-semibold text-gray-800 dark:text-white">Reason</h4>
                            <div className="min-h-[100px] max-h-[120px] overflow-y-auto rounded border border-amber-200 bg-white/50 p-3 text-xs dark:border-amber-600 dark:bg-gray-800/50">
                                {request.reason || "No reason provided for this request."}
                            </div>
                        </div>
                        <div className="rounded-lg border border-gray-200 bg-gradient-to-br from-gray-50 to-slate-50 p-4 dark:border-gray-600 dark:from-gray-800 dark:to-slate-800">
                            <h4 className="mb-2 text-xs font-semibold">
                                Approval Notes{" "}
                                <span className="font-normal text-muted-foreground">
                                    {canDecide ? "(required to reject)" : "(Optional)"}
                                </span>
                            </h4>
                            <Textarea
                                value={notes}
                                onChange={(event) => onNotesChange(event.target.value)}
                                placeholder={canDecide
                                    ? "Add comments for approval, or a reason if you reject..."
                                    : "Add your comments or notes for this approval decision..."}
                                rows={3}
                                showCharCount
                                maxLength={250}
                                disabled={!canDecide}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </DialogModal>
    );
};
